/**
 * L1: hash routes. The hash is the only URL state; search text and sheets live in AppState.
 *
 * Item routes carry an opaque token, never the item id (CSO review of PR #95). The URL is written
 * into Safari's history, and once J-3705 loads real wallets an item id or title is the name of a
 * secret. `ItemTokens` maps tokens to items in memory only, and the shell clears it on every lock,
 * so a token from a reload, a bookmark, a shared link or an earlier session opens nothing.
 */
import { CATEGORIES, type Route } from "../contract.js";

const LISTS = new Set<string>(["all", "favorites", "archive", "deleted", ...CATEGORIES]);

const TOKEN_DIGITS = 12;
const TOKEN = new RegExp(`^\\d{${TOKEN_DIGITS}}$`);
const MAX_DRAWS = 32;

/**
 * Per-session item tokens: random digits, one per item, held in memory. Digits only, so a token
 * can never spell a word from a title. Math.random is enough: a token hides a name from the URL,
 * it is not a credential, and the map behind it lives only as long as the unlocked session.
 */
export class ItemTokens {
  private readonly toItem = new Map<string, string>();
  private readonly toToken = new Map<string, string>();

  constructor(private readonly random: () => number = Math.random) {}

  tokenFor(itemId: string): string {
    const known = this.toToken.get(itemId);
    if (known) return known;
    for (let i = 0; i < MAX_DRAWS; i++) {
      const token = String(Math.floor(this.random() * 10 ** TOKEN_DIGITS)).padStart(TOKEN_DIGITS, "0");
      if (this.toItem.has(token)) continue;
      this.toItem.set(token, itemId);
      this.toToken.set(itemId, token);
      return token;
    }
    throw new Error("router: could not draw an unused item token");
  }

  itemFor(token: string): string | undefined {
    return TOKEN.test(token) ? this.toItem.get(token) : undefined;
  }

  clear(): void {
    this.toItem.clear();
    this.toToken.clear();
  }
}

export function parseHash(hash: string, tokens: Pick<ItemTokens, "itemFor">): Route {
  const parts = hash.replace(/^#\/?/, "").split("/");
  switch (parts[0]) {
    case "home":
      return { name: "home" };
    case "items": {
      const list = parts[1] ?? "all";
      return { name: "items", list: (LISTS.has(list) ? list : "all") as Route["list"] };
    }
    case "search":
      return { name: "search" };
    case "settings":
      return { name: "settings" };
    case "lock":
      return { name: "lock" };
    case "item": {
      const itemId = parts.length === 2 ? tokens.itemFor(parts[1]) : undefined;
      return itemId ? { name: "item", itemId } : { name: "home" };
    }
    default:
      return { name: "home" };
  }
}

export function formatRoute(route: Route, tokens: Pick<ItemTokens, "tokenFor">): string {
  switch (route.name) {
    case "items":
      return `#/items/${route.list ?? "all"}`;
    case "item":
      return route.itemId ? `#/item/${tokens.tokenFor(route.itemId)}` : "#/home";
    default:
      return `#/${route.name}`;
  }
}

export type TabId = "home" | "items" | "search";

/** Which tab is selected for a route. Settings opens from the switcher on Home. */
export function tabOf(route: Route): TabId {
  if (route.name === "items" || route.name === "item") return "items";
  if (route.name === "search") return "search";
  return "home";
}
